import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Check, ExternalLink, Loader2 } from 'lucide-react';
import {
  buildExitCalldata, connectArc, discoverWallets, pickWallet, pickedWallet,
  readV4Position, sendExit, simulateExit, v4TokenId, waitReceipt,
  type V4Position, type WalletChoice,
} from '../lib/arcExit';

// Arc Uniswap v4 pozisyonundan arayüzsüz çıkış.
// Sıra: cüzdan bağla → zincirden pozisyonu oku (sahip/likidite/hook) → oran seç →
//       SİMÜLE et → imzala → makbuzu bekle → LP Trax senkronunu tetikle.
// İmza kullanıcıda; burada yalnız calldata kurulur.

interface Props {
  dark?: boolean;
  positionId: string;                 // 'arc-v4:<tokenId>'
  wallet: string;                     // LP Trax'in izlediği cüzdan
  label: string;                      // 'USDC / LIFT'
  curA: number; curB: number; symbolA: string; symbolB: string;
  onClose: () => void;
  onDone: () => void;                 // başarıyla çekildi → senkron tetikle
}

export default function ArcExitModal({
  dark = true, positionId, wallet, label, curA, curB, symbolA, symbolB, onClose, onDone,
}: Props) {
  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';

  const tokenId = v4TokenId(positionId);
  const [wallets, setWallets] = useState<WalletChoice[]>([]);
  const [picked, setPicked]   = useState<WalletChoice | null>(null);
  const [acct, setAcct]   = useState<string | null>(null);
  const [pos, setPos]     = useState<V4Position | null>(null);
  const [pct, setPct]     = useState(100);
  const [busy, setBusy]   = useState<string | null>(null);
  const [err, setErr]     = useState<string | null>(null);
  const [gas, setGas]     = useState<bigint | null>(null);
  const [hash, setHash]   = useState<string | null>(null);
  const [done, setDone]   = useState(false);

  // Cüzdan bağlanınca pozisyonu zincirden oku (backend'e değil zincire güven).
  const connect = async () => {
    setErr(null); setBusy('Cüzdan bağlanıyor…');
    try {
      const a = await connectArc();
      setAcct(a);
      setBusy('Pozisyon okunuyor…');
      if (tokenId != null) setPos(await readV4Position(tokenId));
    } catch (e: any) { setErr(e?.message || String(e)); }
    setBusy(null);
  };

  // Kurulu cüzdanları EIP-6963 ile keşfet; MetaMask varsa onu öne al.
  // (window.ethereum'u son kurulan cüzdan ele geçiriyor — Trust kendini
  //  MetaMask gibi de tanıtabildiği için bayrağa değil rdns'e bakıyoruz.)
  useEffect(() => {
    let alive = true;
    discoverWallets().then(ws => {
      if (!alive) return;
      setWallets(ws);
      const p = pickedWallet();
      setPicked(p);
      if (p) connect();
    });
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, []);

  const choose = (w: WalletChoice) => {
    pickWallet(w); setPicked(w);
    setAcct(null); setPos(null); setErr(null); setGas(null); setHash(null); setDone(false);
    connect();
  };

  const mismatch = !!acct && !!pos && pos.owner.toLowerCase() !== acct.toLowerCase();
  const empty    = !!pos && pos.liquidity === 0n;
  const ready    = !!acct && !!pos && !mismatch && !empty && !done;

  const run = async () => {
    if (!ready || tokenId == null || !pos || !acct) return;
    setErr(null); setHash(null);
    try {
      const liq = pct === 100 ? null : (pos.liquidity * BigInt(pct)) / 100n;
      const data = buildExitCalldata({
        tokenId, liquidity: liq,
        currency0: pos.currency0, currency1: pos.currency1,
        to: acct, deadline: Math.floor(Date.now() / 1000) + 1800,
      });
      setBusy('Simüle ediliyor…');
      const g = await simulateExit(acct, data);
      setGas(g);
      setBusy('Cüzdanda imza bekleniyor…');
      const h = await sendExit(acct, data, g);
      setHash(h);
      setBusy('İşlem onayı bekleniyor…');
      const rc = await waitReceipt(h);
      setBusy(null);
      if (rc && BigInt(rc.status || '0x0') === 1n) { setDone(true); onDone(); }
      else if (rc) setErr('İşlem zincirde başarısız oldu (status 0).');
    } catch (e: any) {
      setBusy(null);
      setErr(e?.data?.message || e?.message || String(e));
    }
  };

  const share = (v: number) => (v * pct) / 100;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className={`w-full max-w-lg rounded-xl border ${card} p-5`} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-semibold">Likiditeyi çek — {label}</div>
            <div className={`text-[11px] ${muted}`}>
              Uniswap arayüzü olmadan, doğrudan v4 PositionManager çağrısı · {positionId}
            </div>
          </div>
          <button onClick={onClose} className={muted}><X size={16} /></button>
        </div>

        {!wallets.length && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-300 mb-3">
            <AlertTriangle size={13} className="inline mr-1" />
            Bu tarayıcıda cüzdan eklentisi görünmüyor. MetaMask/Rabby kurulu bir Chrome
            penceresinde bu sayfayı açın (eklenti <b>file://</b> sayfalarına enjekte olmaz).
          </div>
        )}

        {wallets.length > 1 && (
          <div className="mb-3">
            <div className={`text-[11px] ${muted} mb-1`}>Cüzdan seç</div>
            <div className="flex gap-2 flex-wrap">
              {wallets.map(w => (
                <button key={w.uuid} onClick={() => choose(w)}
                  className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] ${
                    picked?.uuid === w.uuid ? 'border-sky-500 bg-sky-500/15 text-sky-300' : `${sub} ${muted}`}`}>
                  {w.icon && <img src={w.icon} alt="" className="w-4 h-4 rounded" />}
                  {w.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!acct && wallets.length > 0 && (
          <button onClick={connect} disabled={!!busy}
            className="w-full rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 py-2 text-sm font-medium mb-3">
            {busy || `${picked?.name || 'Cüzdanı'} bağla (Arc 5042)`}
          </button>
        )}

        {pos && (
          <div className={`rounded-lg border ${sub} p-3 text-[12px] space-y-1 mb-3`}>
            <Row muted={muted} k={`Bağlı cüzdan${picked ? ` (${picked.name})` : ''}`} v={acct || '—'} mono
              bad={mismatch} note={mismatch ? 'pozisyon sahibi değil' : undefined} />
            <Row muted={muted} k="Pozisyon sahibi" v={pos.owner} mono />
            <Row muted={muted} k="Havuz" v={`ücret %${pos.fee / 10000} · spacing ${pos.tickSpacing}`} />
            <Row muted={muted} k="Hook" v={BigInt(pos.hooks) === 0n ? 'yok (düz havuz)' : pos.hooks} mono />
            <Row muted={muted} k="Aralık" v={`tick ${pos.tickLower} … ${pos.tickUpper}`} />
            <Row muted={muted} k="Likidite" v={pos.liquidity.toString()} mono />
          </div>
        )}

        {mismatch && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-300 mb-3">
            Cüzdan eşleşmiyor. LP Trax bu pozisyonu <span className="font-mono">{wallet.slice(0, 10)}…</span> için
            izliyor; çekim yalnız NFT sahibi cüzdanla imzalanabilir.
          </div>
        )}
        {empty && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-300 mb-3">
            Likidite sıfır — çekilecek bakiye yok.
          </div>
        )}

        {ready && (
          <>
            <div className="flex gap-2 mb-2">
              {[25, 50, 100].map(v => (
                <button key={v} onClick={() => { setPct(v); setGas(null); }}
                  className={`flex-1 rounded-lg border py-1.5 text-[12px] font-medium ${
                    pct === v ? 'border-sky-500 bg-sky-500/15 text-sky-300' : `${sub} ${muted}`}`}>
                  %{v}
                </button>
              ))}
            </div>
            <div className={`text-[11px] ${muted} mb-3`}>
              Beklenen çıktı ≈ <b>{share(curA).toLocaleString('tr-TR', { maximumFractionDigits: 6 })} {symbolA}</b>
              {' + '}<b>{share(curB).toLocaleString('tr-TR', { maximumFractionDigits: 6 })} {symbolB}</b>
              {pct === 100
                ? ' · birikmiş ücret dahil, NFT yakılır.'
                : ' · NFT durur, ücretler havuzda kalır.'}
              {' '}Alt sınır konmuyor (amountMin = 0); aralık dışı tek taraflı pozisyonda etkisi yok.
              {gas != null && <> · tahmini gaz <b>{gas.toString()}</b></>}
            </div>
            <button onClick={run} disabled={!!busy}
              className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 py-2 text-sm font-semibold flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />}
              {busy || (pct === 100 ? 'Tamamını çek ve NFT\'yi yak' : `%${pct} çek`)}
            </button>
          </>
        )}

        {hash && (
          <a href={`https://explorer.arc.io/tx/${hash}`} target="_blank" rel="noreferrer"
            className="mt-3 flex items-center gap-1 text-[11px] text-sky-400 hover:underline font-mono break-all">
            {hash} <ExternalLink size={10} />
          </a>
        )}
        {done && (
          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12px] text-emerald-300">
            <Check size={13} className="inline mr-1" /> Çekim onaylandı. LP Trax senkronu tetiklendi;
            pozisyon bir sonraki taramada kapanmış olarak görünecek.
          </div>
        )}
        {err && (
          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-300 break-words">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, muted, mono, bad, note }: {
  k: string; v: string; muted: string; mono?: boolean; bad?: boolean; note?: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className={`${muted} w-28 shrink-0`}>{k}</span>
      <span className={`${mono ? 'font-mono text-[11px]' : ''} ${bad ? 'text-red-400' : ''} break-all`}>
        {v}{note && <span className="text-red-400"> · {note}</span>}
      </span>
    </div>
  );
}
